<script lang="ts">
import { defineComponent, inject, onMounted, PropType, ref, watch } from "vue";
import Beitrag from "@/components/Beitrag.vue";
import Steps from "@/components/Steps.vue";
import formatUtils from "@/businesslogic/utils/formatUtils";
import formValidierung from "@/businesslogic/formValidierung";
import Produktmodul from "@/components/module/Produktmodul.vue";
import ProduktmodulePHV from "@/components/module/produktmodulePHV";
import Leistungstabelle from "@/components/leistungstabelle/Leistungstabelle.vue";
import LeistungenPHVDirekt from "@/components/leistungstabelle/LeistungenPHVDirekt";
import LeistungenPHVMakler from "@/components/leistungstabelle/LeistungenPHVMakler";
import Tarifauswahl from "@/components/tarif/Tarifauswahl.vue";
import TarifauswahlInformationenPHV from "@/components/tarif/TarifauswahlInformationenPHV";
import Tarifoptionen from "@/components/tarifoptionen/Tarifoptionen.vue";
import TarifoptionInformationenPHV from "@/components/tarifoptionen/TarifoptionInformationenPHV";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import EmailOderHerunterladen from "@/components/vorschlag/EmailOderHerunterladen.vue";
import { NavvItem } from "@/model/NavItem";
import Maklerrabatt from "@/components/maklerrabatt/Maklerrabatt.vue";
import Logger from "js-logger";
import HugRabatt from "@/components/HugRabatt.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "VergleichPHV",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    HugRabatt,
    Maklerrabatt,
    EmailOderHerunterladen,
    ValidierungsFehler,
    Produktmodul,
    Leistungstabelle,
    Tarifauswahl,
    Tarifoptionen,
    Steps,
    Beitrag,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
  },
  setup(props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");

    const leistungenDirekt = ref(LeistungenPHVDirekt);
    const leistungenMakler = ref(LeistungenPHVMakler);
    const aktiveLinie = ref(1);
    const tarifauswahlInformationen = ref(TarifauswahlInformationenPHV);
    const tarifoptionInformationen = ref(TarifoptionInformationenPHV);
    const { formatCurrency } = formatUtils();
    const { validiereRabatt, validiereForm, getFormFehlerAnzahl, getFormFehler, validiereVorschaeden } =
      formValidierung();

    const fehler = ref({} as any);

    const produktmodule = ref(ProduktmodulePHV);
    let validiert = false;

    function istGewaehlteProduktLinie(produktLinie: string) {
      return piniaStore.state.tarifinformationen.produktLinie === produktLinie;
    }

    function wechsleTarif() {
      if (piniaStore.state.tarifinformationen.produktLinie === "001") {
        aktiveLinie.value = 1;
      } else if (piniaStore.state.tarifinformationen.produktLinie === "002") {
        aktiveLinie.value = 2;
      } else if (piniaStore.state.tarifinformationen.produktLinie === "003") {
        aktiveLinie.value = 3;
      } else {
        Logger.debug("wechsleTarif(): Unbekannte Produktlinie:" + piniaStore.state.tarifinformationen.produktLinie);
      }
      validiereRabatt(piniaStore.state, fehler, validiert);
    }

    function eingabenSindValide() {
      return new Promise((resolve) => {
        validiereForm("phForm").then(() => {
          Logger.debug("Anzahl Fehler aus Validierung: " + getFormFehlerAnzahl());
          fehler.value = getFormFehler();

          validiereVorschaeden(piniaStore.state, fehler);
          validiert = true;
          validiereRabatt(piniaStore.state, fehler, validiert);

          Logger.debug("Fehler-Objekt: ", fehler.value);
          resolve(getFormFehlerAnzahl() === 0);
        });
      });
    }

    function nextPage() {
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          emit("next-page", {
            pageIndex: 1,
          });
        } else {
          document.querySelector("div#tariffOptionsTable")?.scrollIntoView({ behavior: "smooth" });
        }
      });
    }

    function getTIB(): string {
      if (piniaStore.state.isMakler) {
        return "https://www.gev-versicherung.de/downloads/pdf/TI-TIB-PH-Protect-2020.pdf";
      }
      return "https://www.gev-versicherung.de/downloads/pdf/TI-TIB-PH-Care-2020.pdf";
    }

    watch(
      () => piniaStore.state.tarifinformationen.vorschaedenAnzahl,
      (wertNeu) => {
        if (wertNeu != -1) {
          fehler.value["vorschaedenAnzahl"] = null;
        }
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.abschlagZuschlagHoehe,
      () => {
        validiert = true;
        validiereRabatt(piniaStore.state, fehler, validiert);
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.produktLinie,
      (tarifNeu, tarifAlt) => {
        if (tarifNeu !== tarifAlt) {
          wechsleTarif();
        }
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.hundehalterhaftpflicht,
      (hhhNeu, hhhAlt) => {
        if (hhhNeu !== hhhAlt) {
          for (const vorschaden of piniaStore.state.vorschaeden) {
            vorschaden.schadenart = "";
          }
        }
      },
    );

    onMounted(() => {
      wechsleTarif();
    });

    return {
      istGewaehlteProduktLinie,
      nextPage,
      getTIB,
      props,
      piniaStore,
      leistungenDirekt,
      leistungenMakler,
      aktiveLinie,
      wechsleSichtbarkeit,
      istSichtbar,
      fehler,
      formatCurrency,
      produktmodule,
      tarifauswahlInformationen,
      tarifoptionInformationen,
      validiert,
    };
  },
});
</script>

<template>
  <Steps v-bind:items="navItems" :active="1" @goto-page="$emit('goto-page', $event)"></Steps>

  <section>
    <tarifauswahl :tarifauswahlInformationen="tarifauswahlInformationen" />
    <leistungstabelle
      :inhalt="piniaStore.state.isMakler ? leistungenMakler : leistungenDirekt"
      :aktiveLinie="aktiveLinie"
      :link="getTIB()"
    />
    <tarifoptionen :tarifoptionInformationen="tarifoptionInformationen" />
    <div v-if="fehler.vorschaedenAnzahl" class="o-grid">
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200 o-grid__cell--offset-3-480"
      >
        <div class="o-grid o-grid--no-y-gutter o-grid--align-flex-end">
          <div class="o-grid__cell o-grid__cell--12">
            <validierungs-fehler :fehler-text="fehler.vorschaedenAnzahl" />
          </div>
        </div>
      </div>
    </div>

    <produktmodul :produktmodule="produktmodule" :zeige-icons="!piniaStore.state.isMakler" />

    <div class="o-grid o-grid--align-middle u-mar-t-2">
      <div class="o-grid__cell o-grid__cell--12 o-grid__cell--3-480 u-text-align-right--480 u-form-grid-nospace-y">
        <h2 class="u-form-heading unset-text-align">Rabatt</h2>
      </div>
      <HugRabatt />
    </div>
    <maklerrabatt :fehler="fehler" />
    <beitrag @next-page="nextPage" />
    <email-oder-herunterladen produkt="phv" tracking-zusatz="vergleich" />
  </section>
</template>
